<template>
<div>
    <div class="appointments-container">
        <div>
            <div>
                <h4>{{$t('nextAppointmentsTitle')}}</h4>
            </div>
            <div class="m-4" v-if="!nextAppointmentsData.length">
                <h5 v-html="$t('emptyNextAppointments')"></h5>
            </div>
            <div class="my-4" v-else>
                <appointment-row-patient v-for="item in nextAppointmentsData" :key="item.appointmentId" :id="item.appointmentId" :serviceType="item.consultancyType" :profPage="item.customPageUrl" :profName="item.doctorName" :patientName="item.patientName" :petName="item.petName" :changeUserEnabled="item.loggedPatientId !== item.patientId" 
                :patientId="item.patientId" :date="item.date" :stateTitle="getNextAppointmentsStateTitle(item)" :actionButtons="getNextAppointmentsActionButtons(item)" :profSearchDisabled="item.searchDisabled" :historyShared="item.historyShared" :profPhoto="item.profPhoto"></appointment-row-patient>
            </div>
        </div>
    </div>
    <base-dialog :value="showConfirmPaymentDialog" :content="getSuccessNotification($route.query.transactionType)"></base-dialog>
    <payment-view modal v-model="showPaymentModal" :paymentType="payment.paymentType" :price="payment.price" :appointmentId="payment.appointmentId" :ePrescriptionAvailable="payment.ePrescriptionAvailable" :copayment="payment.copayment" @loadAppointments="loadAppointments()" @closePaymentModal="showPaymentModal = false"></payment-view>
</div>
</template>

<script>
import AppointmentsFilters from '@/components/webapp/patient/appointments/AppointmentsFilters'
import AppointmentRowPatient from '@/components/shared/AppointmentRowPatient'
import appointmentsRepository from '@/repositories/AppointmentsRepository'
import BaseDialog from '@/components/base/BaseDialog'
import appointmentsMixin from '@/mixins/appointments.mixin'
import PaymentView from '@/components/payment/PaymentView'
import { PAYED, POSTPONED, FILLING_REPORT } from '@/constants/db/consultationStates'
import { CALL, WRITTEN, VIDEO, IMMEDIATE_VIDEO, INPLACE } from '@/constants/consultationTypes'
import { PENDING_PAYMENT, APPOINTMENT_ERROR, GIFT_SERVICE, CHANGE_PAYMENT_METHOD, FLAT_RATE, SERVICES_PACKAGES } from '@/constants/paymentTypes'
import { APPOINTMENT_CANCELED, NEW_APPOINTMENT } from '@/constants/signalRCommands'
import i18nMixin from '@/mixins/i18n.mixin'
import { utcDateToUtcFormat } from '@/helpers/date.helpers'

const MENTTUM_CODE = 'MENTTUM_API_USER_CODE'
const LATER_MINUTES_TO_GO_VIDEO = 120
const PREVIOUS_MINUTES_TO_GO_VIDEO = 30
const PREVIOUS_MINUTES_TO_CANCEL_APPOINTMENT = 120
const PREVIOUS_MINUTES_TO_CANCEL_COMPANY_APPOINTMENT = 5
const INTERVAL_REFRESH_APPOINTMENTS = 120000
const I18N_PAYMENTS_FILE = 'payments'

export default {
    components: {
        AppointmentRowPatient,
        AppointmentsFilters,
        PaymentView,
        BaseDialog
    },
    mixins: [appointmentsMixin, i18nMixin(I18N_PAYMENTS_FILE)],
    data () {
      return {
        showPaymentModal: false,
        nextAppointmentsData: [],
        showConfirmPaymentDialog: false,
        refreshAppointmentsInterval: null
      }
    },
    created () {
        if (this.$route.query.paymentSuccess === 'true') {
            this.showConfirmPaymentDialog = true
            this.$store.commit('appointmentRequest/reset')
        } else if (this.$route.query.paymentSuccess === 'false') {
            this.$store.dispatch('showDangerAlert', this.$t('defaultErrorMessage'))
        }
        window.history.replaceState(null, '', window.location.pathname)
        this.loadAppointments()
    },
    mounted () {
        this.refreshAppointmentsInterval = setInterval(() => {
            this.loadAppointments(true)
        }, INTERVAL_REFRESH_APPOINTMENTS)
    },
    destroyed () {
        clearInterval(this.refreshAppointmentsInterval)
    },
    methods: {
        loadAppointments (hideLoadingIcon) {
            appointmentsRepository.getNextPatientAppointments(hideLoadingIcon).then(response => {
                if (response.data.data) {
                    let nextAppointmentsData = response.data.data.map(item => {
                        item.customPageUrl = this.$store.state.session.user.companyCode && this.$store.state.session.user.companyCode.code === MENTTUM_CODE ? this.$helpers.getMenttumUrl() : this.$helpers.getCustomPageUrl(item.customPageUrl)
                        item.date = utcDateToUtcFormat(item.date)
                        return item
                    })
                    this.nextAppointmentsData = nextAppointmentsData.filter(item => item.consultancyType === VIDEO || item.consultancyType === IMMEDIATE_VIDEO || item.consultancyType === INPLACE).sort((a, b) => a.date - b.date)
                    this.nextAppointmentsData.push(...nextAppointmentsData.filter(item => !this.nextAppointmentsData.some(app => app.appointmentId === item.appointmentId)).sort((a, b) => a.date - b.date))
                } else {
                    this.resetData()
                }
            })
        },
        getNextAppointmentsStateTitle (item) {
            if (item.consultancyType === CALL) {
                return item.appointmentState === POSTPONED ? this.$t('nextAppointmentsStates.postponedCall') : this.$t('nextAppointmentsStates.waitingCall')
            } else if (item.consultancyType === WRITTEN) {
                return this.getWrittenAppointmentsStateTitle(item)
            }
        },
        getNextAppointmentsActionButtons (item) {
            let actions = []
            if (item.loggedPatientId !== item.patientId) return actions
            if (item.consultancyType === WRITTEN) return this.getWrittenActionButtons(item)
            if (this.unpaidAppointment(item)) actions.push(this.paymentAction(item))
            if ((item.consultancyType === IMMEDIATE_VIDEO || item.consultancyType === VIDEO) && (item.date - Date.now()) / 60000 <= PREVIOUS_MINUTES_TO_GO_VIDEO && (Date.now() - item.date) / 60000 <= LATER_MINUTES_TO_GO_VIDEO && item.appointmentState !== FILLING_REPORT) {
                if (item.consultancyType === IMMEDIATE_VIDEO && item.doctorName) actions.push(this.goVideoAction(item, true))
                if (item.consultancyType === VIDEO && item.doctorName && item.appointmentState <= PAYED && (item.price > 0 && !item.noChargeAppointment ? item.anyPayment || (item.withCompaniesServices && !item.copayment) : true)) actions.push(this.goVideoAction(item))
            }
            if (item.doctorName) actions.push(this.showAppointmentAction(item))
            if (item.appointmentState !== FILLING_REPORT && (((item.consultancyType === VIDEO || item.consultancyType === IMMEDIATE_VIDEO) && ((Date.now() - item.date) / 60000 <= -(item.withCompaniesServices ? PREVIOUS_MINUTES_TO_CANCEL_COMPANY_APPOINTMENT : PREVIOUS_MINUTES_TO_CANCEL_APPOINTMENT) && !item.videoAttended)) || ((item.consultancyType !== VIDEO && item.consultancyType !== IMMEDIATE_VIDEO) && item.date >= Date.now()))) actions.push(this.cancelAppointmentAction(item))
            return actions
        },
        resetData () {
            this.nextAppointmentsData = []
        },
        getSuccessNotification (transactionType) {
            switch (parseInt(transactionType)) {
                case PENDING_PAYMENT:
                    return this.$t('payments.confirmPendingPayment')
                case APPOINTMENT_ERROR:
                    return this.$t('payments.confirmErrorPayment')
                case CHANGE_PAYMENT_METHOD:
                    return this.$t('payments.confirmChangePaymentMethod')
                case GIFT_SERVICE:
                    return this.$t('payments.confirmGiftPayment')
                case FLAT_RATE:
                    return this.$t('payments.confirmFlatRatePayment')
                case SERVICES_PACKAGES:
                    return this.$t('payments.confirmServicesPackagesPayment')
                default:
                    return this.$route.query.companyQueue === 'true' ? this.$t('companyQueueAppointmentSended') : this.$t('payments.successPayment')
            }
        }
    },
    computed: {
    },
    watch: {
        '$store.state.signalr.commandReceived' ({ command, data }) {
            switch (command) {
                case APPOINTMENT_CANCELED:
                case NEW_APPOINTMENT:
                    this.loadAppointments(true)
                    break
            }
        }
    }
}
</script>


// WEBPACK FOOTER //
// src/components/webapp/patient/appointments/NextAppointments.vue